import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import LinkifiedText from '@/utils/linkify';
import { showToast } from '@/config/toast.config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Receipt as ReceiptIcon,
  Car as CarIcon,
  Storefront as StorefrontIcon,
  FilePdf as FilePdfIcon,
} from '@phosphor-icons/react';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import TaskSkuImagePreview from '@/pages/CRMTasks/components/TaskSkuImagePreview';
import { currencyIdToCode } from '@/config/status.config';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Format price with Romanian locale
const formatPrice = (price, currency = 'RON') => {
  const parsed = Number(price);
  if (price == null || price === '' || !Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return `${parsed.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} ${currency}`;
};

// Format date as DD/MM/YYYY
const formatDateRo = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return null;
  }
};

// Brand-colored link styling for note URLs on the public offer page.
const NOTE_LINK_CLASS = 'text-brand-primary underline hover:text-brand-primary/80 break-words';

// The first photo is the cover, so the thumbnail gets the green cover border.
const withCoverImage = (images) => images.map((img, idx) => ({ ...img, isCover: idx === 0 }));

// Build vehicle display name
const getVehicleDisplayName = (v) => {
  if (!v) return 'Vehicul nespecificat';
  const parts = [
    v.manufacturer_name || null,
    v.model_name || null,
    v.build_year || null,
    v.engine_name || null,
    (v.engine_code_name && String(v.engine_code_name).trim().length > 0) ? v.engine_code_name : null,
    (v.engine_type_name && String(v.engine_type_name).trim().length > 0) ? v.engine_type_name : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Vehicul nespecificat';
};

export default function PublicQuoteViewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // PDF generation state
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Download PDF offer
  const handleDownloadPdf = async () => {
    if (!data) return;

    setIsGeneratingPdf(true);
    try {
      // Build image counts from the data we already have
      const imageCounts = {};
      (data.line_items || []).forEach(li => {
        imageCounts[li.id] = {
          total: (li.images || []).length,
        };
      });

      // Group items by vehicle
      const vehicleById = Object.fromEntries((data.vehicles || []).map(v => [String(v.id), v]));
      const grouped = new Map();
      (data.line_items || []).forEach(li => {
        const vid = String(li.vehicle?.id || 'none');
        if (!grouped.has(vid)) grouped.set(vid, []);
        grouped.get(vid).push(li);
      });
      const vehicleOrder = (data.vehicles || []).map(v => String(v.id));
      const groupKeys = Array.from(grouped.keys()).sort((a, b) => {
        const aIdx = a === 'none' ? Infinity : vehicleOrder.indexOf(a);
        const bIdx = b === 'none' ? Infinity : vehicleOrder.indexOf(b);
        return aIdx - bIdx;
      });

      // Build groups array for PDF
      const groups = groupKeys.map(vid => {
        const v = vehicleById[vid];
        const vehicleName = getVehicleDisplayName(v);
        const items = (grouped.get(vid) || []).map(li => ({
          id: li.id,
          name: li.component_name || `Articol #${li.id}`,
          quantity: li.quantity || 1,
          price: li.price,
          currency: currencyIdToCode(li.currency_id),
          publicNote: li.public_note || '',
          outOfStock: li.available === false,
          uzuraValue: 0,
        }));
        return { vehicle: vehicleName, items };
      });

      // Offer-level currency: derived from the first priced line item (a quote
      // is single-currency by convention; EU-storefront quotes are EUR).
      const firstPricedItem = (data.line_items || []).find(li => li.price != null);
      const offerCurrency = currencyIdToCode(firstPricedItem?.currency_id);

      // Build offer data for PDF
      const offerData = {
        companyName: data.contact_name_masked || '',
        offerDate: formatDateRo(data.created_at) || '',
        quoteNumber: data.id || '',
        currency: offerCurrency,
        groups,
        imageCounts,
      };

      // Current page URL as the offer link
      const offerLink = window.location.href;

      // Generate PDF. The renderer is ~1.5 MB, so it loads only on this click.
      const [{ pdf }, { default: DezOfferPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/components/Quotes/DezOfferPdfDocument'),
      ]);
      const doc = <DezOfferPdfDocument offer={offerData} offerLink={offerLink} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const fileName = `Oferta-dezShop-${data.id || ''}-${Date.now().toString(36)}.pdf`;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      showToast.error(error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        if (!API_BASE) {
          throw new Error('VITE_API_BASE_URL nu este setată');
        }

        // Set page metadata
        try {
          document.title = 'dezShop - Ofertă piese';
        } catch { /* ignore */ }

        // Start the quote request alongside the status check (saves a round
        // trip on slow mobile data). The status still decides first; the
        // backend does not count views for expired or revoked links.
        const noCountParam = (() => {
          const v = String(searchParams.get('no_count') || '').toLowerCase();
          return v === '1' || v === 'true' || v === 'yes';
        })();
        const quotePromise = fetch(`${API_BASE}/public/quotes/${encodeURIComponent(token)}${noCountParam ? '?no_count=1' : ''}`);
        quotePromise.catch(() => { /* handled below, or ignored when the status redirects */ });

        // Check token status first
        try {
          const statusRes = await fetch(`${API_BASE}/public/quotes/${encodeURIComponent(token)}/status`);
          if (statusRes.ok) {
            const statusJson = await statusRes.json();
            const st = String(statusJson?.status || '').toLowerCase();
            if (st === 'expired') {
              navigate('/expired', { replace: true });
              return;
            }
            if (st === 'invalid' || st === 'revoked') {
              navigate('/expired', { replace: true });
              return;
            }
            // Note: 'submitted' is allowed for view-only pages
          }
        } catch (error) {
          showToast.error(error);
          navigate('/info', { replace: true });
          return;
        }

        // Quote data
        const res = await quotePromise;
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(body?.detail || `HTTP ${res.status}`);
          err.status = res.status;
          if (res.status === 404 || res.status === 410) {
            navigate('/expired', { replace: true });
          } else {
            navigate('/info', { replace: true });
          }
          throw err;
        }
        const json = await res.json();
        if (isMounted) setData(json);
      } catch (error) {
        showToast.error(error);
        try { navigate('/info', { replace: true }); } catch { /* ignore */ }
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [token, searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data) return null;

  // Group line items by vehicle
  const vehicleById = Object.fromEntries((data.vehicles || []).map(v => [String(v.id), v]));
  const grouped = new Map();
  (data.line_items || []).forEach(li => {
    const vid = String(li.vehicle?.id || 'none');
    if (!grouped.has(vid)) grouped.set(vid, []);
    grouped.get(vid).push(li);
  });
  const vehicleOrder = (data.vehicles || []).map(v => String(v.id));
  // Sort groups: vehicles in order first, then items without vehicle ('none') at the end
  const groupKeys = Array.from(grouped.keys()).sort((a, b) => {
    const aIdx = a === 'none' ? Infinity : vehicleOrder.indexOf(a);
    const bIdx = b === 'none' ? Infinity : vehicleOrder.indexOf(b);
    return aIdx - bIdx;
  });

  const createdDate = formatDateRo(data.created_at);

  return (
    <div className="w-full mx-auto px-3 sm:px-6 py-4 font-sans max-w-[900px]">
      {/* Header section - Quote info */}
      <section className="mb-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                <ReceiptIcon size={28} weight="duotone" className="text-brand-primary" />
                <span>Ofertă piese</span>
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? (
                  <>
                    <LoadingSpinner size={16} className="mr-2" />
                    Se generează...
                  </>
                ) : (
                  <>
                    <FilePdfIcon size={18} weight="bold" className="mr-2" />
                    Descarcă PDF
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {/* Quote ID */}
              <div className="flex items-center justify-between sm:justify-start gap-2">
                <span className="text-gray-500">Nr. cerere:</span>
                <span className="font-semibold text-gray-800">{data.id}</span>
              </div>
              
              {/* Date */}
              {createdDate && (
                <div className="flex items-center justify-between sm:justify-start gap-2">
                  <span className="text-gray-500">Data:</span>
                  <span className="font-semibold text-gray-800">{createdDate}</span>
                </div>
              )}

              {/* Contact name */}
              {data.contact_name_masked && (
                <div className="flex items-center justify-between sm:justify-start gap-2">
                  <span className="text-gray-500">Beneficiar:</span>
                  <span className="font-semibold text-gray-800">{data.contact_name_masked}</span>
                </div>
              )}

              {/* Contact phone */}
              {data.contact_phone_masked && (
                <div className="flex items-center justify-between sm:justify-start gap-2">
                  <span className="text-gray-500">Telefon:</span>
                  <span className="font-semibold text-gray-800">{data.contact_phone_masked}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Line items grouped by vehicle */}
      <section className="space-y-6">
        {groupKeys.map((vid) => {
          const v = vehicleById[vid];
          const items = grouped.get(vid) || [];
          const vehicleName = getVehicleDisplayName(v);

          return (
            <Card key={vid}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
                  <CarIcon size={22} weight="duotone" className="text-brand-primary flex-shrink-0" />
                  <span className="whitespace-normal break-words leading-snug">
                    {vehicleName}
                  </span>
                </CardTitle>
                {/* Vehicle details */}
                {v && (v.vin_number || v.body_type) && (
                  <div className="mt-2 ml-9 text-sm text-gray-500 space-y-0.5">
                    {v.vin_number && (
                      <div>VIN: <span className="font-medium text-gray-700">{v.vin_number}</span></div>
                    )}
                    {v.body_type && (
                      <div>Caroserie: <span className="font-medium text-gray-700">{v.body_type}</span></div>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-4">
                {/* Table header */}
                <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-3 py-2 bg-gray-50 rounded-t-lg border border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  <div className="col-span-6">Piesă</div>
                  <div className="col-span-2 text-center">Imagini</div>
                  <div className="col-span-2 text-center">Cant.</div>
                  <div className="col-span-2 text-right">Preț unitar</div>
                </div>

                {/* Mobile layout - card per item */}
                <div className="sm:hidden space-y-3">
                  {items.map((li) => {
                    const componentName = li.component_name || li.component_id ||
                      (String(li?.line_item_type || '') === 'vehicle_sale' ? 'Vehicul pentru dezmembrare' : 'Piesă nespecificată');
                    const quantity = Number(li.quantity ?? 1);
                    const liCurrency = currencyIdToCode(li.currency_id);
                    const priceFormatted = formatPrice(li.price, liCurrency);
                    const hasPrice = priceFormatted !== null;
                    const outOfStock = li.available === false;
                    const hasImages = Array.isArray(li.images) && li.images.length > 0;
                    const unitPrice = Number(li.price) || 0;
                    const totalPrice = quantity > 1 && hasPrice ? formatPrice(unitPrice * quantity, liCurrency) : null;

                    return (
                      <div 
                        key={li.id} 
                        className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                      >
                        {/* Component name and public note */}
                        <div className={li.public_note ? 'mb-1' : 'mb-2'}>
                          <span className="font-medium text-gray-800 leading-snug">
                            {componentName}
                          </span>
                        </div>
                        {li.public_note && (
                          <div className="mb-2 text-xs text-gray-500 italic break-words">
                            <LinkifiedText text={li.public_note} className={NOTE_LINK_CLASS} />
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          {/* Part photo; the badge counts the rest, a tap opens the gallery */}
                          {hasImages && (
                            <TaskSkuImagePreview images={withCoverImage(li.images)} alt={componentName} thumbSize={88} />
                          )}

                          {/* Details grid */}
                          <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                            {/* Quantity */}
                            <div className="text-gray-500">Cantitate:</div>
                            <div className="font-medium text-gray-700">{quantity} buc</div>

                            {/* Unit price */}
                            <div className="text-gray-500">Preț/buc:</div>
                            <div className={`font-medium ${outOfStock ? 'text-red-700' : (hasPrice ? 'text-gray-700' : 'text-gray-400')}`}>
                              {outOfStock ? 'fără stoc' : (hasPrice ? priceFormatted : '-')}
                            </div>

                            {/* Total price (only if qty > 1) */}
                            {totalPrice && (
                              <>
                                <div className="text-gray-500">Total:</div>
                                <div className="font-semibold text-gray-800">{totalPrice}</div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Shop link */}
                        {li.shop_link && (
                          <div className="mt-3 flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                            >
                              <a
                                href={li.shop_link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <StorefrontIcon size={16} />
                                Vezi în magazin
                              </a>
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop layout - table */}
                <div className="hidden sm:block">
                <div className="divide-y divide-gray-100 border-x border-b border-gray-200 rounded-b-lg">
                  {items.map((li, idx) => {
                    const componentName = li.component_name || li.component_id ||
                      (String(li?.line_item_type || '') === 'vehicle_sale' ? 'Vehicul pentru dezmembrare' : 'Piesă nespecificată');
                    const quantity = Number(li.quantity ?? 1);
                    const priceFormatted = formatPrice(li.price, currencyIdToCode(li.currency_id));
                    const hasPrice = priceFormatted !== null;
                    const outOfStock = li.available === false;
                    const hasImages = Array.isArray(li.images) && li.images.length > 0;

                    return (
                      <div 
                        key={li.id} 
                        className={`px-3 py-3 ${idx === items.length - 1 ? 'rounded-b-lg' : ''}`}
                      >

                        {/* Desktop row */}
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6 min-w-0">
                            <span className="font-medium text-gray-800 leading-snug block truncate">
                              {componentName}
                            </span>
                            {li.public_note && (
                              <span className="text-xs text-gray-600 italic block mt-0.5 break-words">
                                <LinkifiedText text={li.public_note} className={NOTE_LINK_CLASS} />
                              </span>
                            )}
                            {li.shop_link && (
                              <a
                                href={li.shop_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-brand-primary underline hover:text-brand-primary/80 transition-colors block"
                              >
                                Vezi în magazin
                              </a>
                            )}
                          </div>
                          {/* Image button - desktop */}
                          <div className="col-span-2 flex justify-center">
                            {hasImages ? (
                              <TaskSkuImagePreview images={withCoverImage(li.images)} alt={componentName} thumbSize={56} />
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </div>
                          <div className="col-span-2 text-center font-medium text-gray-700">
                            {quantity}
                          </div>
                          <div className={`col-span-2 text-right font-semibold ${outOfStock ? 'text-red-700' : (hasPrice ? 'text-gray-800' : 'text-gray-400')}`}>
                            {outOfStock ? 'fără stoc' : (hasPrice ? priceFormatted : '-')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* Footer notice */}
      <div className="mt-4 text-sm text-gray-500">
        <p className="text-right text-xs">Prețurile includ TVA. Oferta este valabilă 7 zile de la emitere, în limita stocului disponibil.</p>
        <div className="border-t border-gray-200 mt-10 pt-3 text-center">
          <p className="text-gray-400">Mulțumim pentru interesul acordat!</p>
        </div>
      </div>

    </div>
  );
}

